import {
  BriefcaseIcon,
  Building2Icon,
  CalendarClockIcon,
  CalendarIcon,
  FlagIcon,
  MapPinIcon,
  MapPinnedIcon,
} from 'lucide-react'
import { Flex, Text, Tooltip } from 'ui'

const CUSTOM_WEEK_TAG = 'custom_week'

type InvoiceGroupType =
  | 'department'
  | 'location'
  | 'location_plus_name'
  | 'position'
  | 'city'
  | 'state'
  | 'date'
  | 'shift'

type InvoiceGroupCellProps = {
  invoiceGroup: string
  invoiceGroupKey: string[]
}

function getPrimaryInvoiceGroupType(
  invoiceGroupKey: string[]
): InvoiceGroupType | null {
  let index = 0
  while (index < invoiceGroupKey.length) {
    const part = invoiceGroupKey[index]
    if (part === CUSTOM_WEEK_TAG) {
      index += 2
      continue
    }
    switch (part) {
      case 'department':
      case 'location':
      case 'location_plus_name':
      case 'position':
      case 'city':
      case 'state':
      case 'date':
      case 'shift':
        return part
      default:
        return null
    }
  }
  return null
}

function getInvoiceGroupTypeMeta(type: InvoiceGroupType) {
  switch (type) {
    case 'department':
      return { label: 'Department', Icon: Building2Icon }
    case 'location':
    case 'location_plus_name':
      return { label: 'Location', Icon: MapPinIcon }
    case 'position':
      return { label: 'Position', Icon: BriefcaseIcon }
    case 'city':
      return { label: 'City', Icon: MapPinnedIcon }
    case 'state':
      return { label: 'State', Icon: FlagIcon }
    case 'date':
      return { label: 'Date', Icon: CalendarIcon }
    case 'shift':
      return { label: 'Shift', Icon: CalendarClockIcon }
    default: {
      const _exhaustive: never = type
      return _exhaustive
    }
  }
}

export function InvoiceGroupCell({
  invoiceGroup,
  invoiceGroupKey,
}: InvoiceGroupCellProps) {
  if (!invoiceGroup) {
    return <Text>—</Text>
  }

  const groupType = getPrimaryInvoiceGroupType(invoiceGroupKey)
  if (groupType === null) {
    return <Text>{invoiceGroup}</Text>
  }

  const { label, Icon } = getInvoiceGroupTypeMeta(groupType)

  return (
    <Flex alignItems="center" style={{ gap: 8, minWidth: 0 }}>
      <Tooltip content={label} variant="neutral">
        <Flex alignItems="center" flexShrink={0} aria-label={label}>
          <Icon size={16} />
        </Flex>
      </Tooltip>
      <Text style={{ whiteSpace: 'nowrap' }}>{invoiceGroup}</Text>
    </Flex>
  )
}
